import { localCoaching, SWARA_NAMES } from '@swarasense/shared';
import type {
  CoachingFocus,
  GamakaProfileEntry,
  PracticeLevel,
  RecordedSample,
  SessionReport,
  SwaraSymbol,
} from '@swarasense/shared';
import { ragaSeedFor } from '../api/seed.js';
import { celebrate } from './celebrate.js';
import { getGrownupPref, setGrownupPref } from './grownup-pref.js';
import { drawReview, mountPlayback, stopPlayback } from './results.js';
import type { SessionRecording } from './results.js';
import { ragaSwarasFor } from './scoring.js';
import { driftSeries, driftTrendDescription, heatmapCells } from './summary-charts.js';
import type { DriftPoint, HeatmapGrade } from './summary-charts.js';

/**
 * Task 3 (encouraging-feedback): the INLINE results panel that fills
 * `#results` below the ribbon stage after a take. The default view is now a
 * warm, NUMBER-FREE child summary (headline + what-went-well + one gentle
 * "let's try next" + the replay); the original
 * analytical UI (three metric cards, per-swara heatmap, drift sparkline) is
 * unchanged but moved behind a "Grown-up view" disclosure, off by default and
 * persisted in `localStorage['swarasense.grownup']` — see `docs/superpowers/
 * specs/2026-08-03-encouraging-feedback-design.md`. The scoring
 * (`SessionReport`), coaching (`localCoaching`), chart derivations
 * (`summary-charts.ts`), and playback mechanics (`results.ts`) are all reused
 * unchanged; only the presentation split is new.
 */
export interface ResultsPanelDeps {
  report: SessionReport;
  recSamples: RecordedSample[];
  saHz: number;
  targetCents: Record<string, number>;
  /** Kept on the deps shape for parity with `scoring.ts`'s `Scoring` (and in
   * case a future pass shades gamaka-eligible swaras on the static review
   * contour the way `ribbon.ts` does live) — `drawReview` doesn't accept it
   * today, so it isn't read here yet. */
  gamakaProfile: Record<string, GamakaProfileEntry>;
  /** Absent when the take produced no on-device recording (e.g. `MediaRecorder`
   * unsupported, or the `__SWARASENSE_TEST__` harness, which never records) —
   * the replay control is simply omitted in that case. */
  recording?: SessionRecording | undefined;
  ragaName: string;
  level: PracticeLevel;
  /** Called after this panel has cleared `#results` and stopped any playback —
   * the caller's job is resetting ITS OWN take state (recSamples/report/
   * recording, revoking the previous recording's blob URL) and re-arming for
   * the next take. */
  onNewTake: () => void;
  /**
   * Task 4: called when the child taps the "let's try next" drill invitation
   * (`targetSwara` is the raw 12-symbol, e.g. `"G"`). This panel decides
   * WHETHER the button is even shown (a focus swara must exist AND be in the
   * current raga's scale — see `renderResultsPanel`'s `drillTargetSwara`
   * below); the caller owns actually launching practice mode
   * (`generateDrill` + `enterPracticeMode`, the same path Task 2's
   * `__SWARASENSE_ENTER_DRILL__` dev hook already exercises) so this
   * presentation module stays free of that orchestration.
   */
  onTryDrill: (targetSwara: string) => void;
}

/** Swara symbols and coaching copy are derived from our own seed data and
 * pure functions (never user input), so plain interpolation into innerHTML
 * is fine — same trust boundary the rest of the app uses for this data. */
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Share (%) at/above which a headline metric reads as clearly good vs. merely
 * okay vs. needing real attention — one shared scale for all three metric
 * cards (in-raga / well-centred / gamaka adherence), so "82%" always means
 * the same thing across cards. */
const METRIC_GOOD_PCT = 85;
const METRIC_WARN_PCT = 60;

/** `inRagaPct` at/above which the child summary adds a ✨ celebration line to
 * the headline — matches `localCoaching`'s own "Well done" tier (its second
 * encouragement bucket starts at 75), so the sparkle and the wording it sits
 * beside always agree on what counts as "went well". */
const CHILD_CELEBRATE_PCT = 75;

function metricClass(pct: number): 'good' | 'warn' | 'bad' {
  if (pct >= METRIC_GOOD_PCT) return 'good';
  if (pct >= METRIC_WARN_PCT) return 'warn';
  return 'bad';
}

/** One of the three headline cards. The number is always printed — colour is
 * a supplementary cue only, never the sole carrier of the grade. */
function metricHtml(label: string, pct: number): string {
  return `<div class="metric"><div class="k">${escapeHtml(label)}</div><div class="val ${metricClass(pct)}">${Math.round(pct)}%</div></div>`;
}

const GRADE_LABEL: Record<HeatmapGrade, string> = {
  excellent: 'Excellent',
  good: 'Good',
  off: 'Off',
  wrong: 'Wrong',
};
const GRADE_CLASS: Record<HeatmapGrade, string> = {
  excellent: 'ex',
  good: 'gd',
  off: 'of',
  wrong: 'wr',
};

/**
 * Per-swara heatmap: a single accessible `<table>` IS the heatmap (Task 6's
 * approach, reproduced here) — one row, one cell per swara, each cell
 * carrying the band TINT background + `--ink` text (via the `.cell.ex/.gd/
 * .of/.wr` classes already styled for the stage/mockup's palette) AND the
 * grade's name + inTune% printed inside the cell — colour is never the sole
 * carrier of the grade, so it reads the same in grayscale or to a screen
 * reader (which gets the cell's own text, no header lookup required).
 */
function heatmapHtml(report: SessionReport): string {
  const cells = heatmapCells(report);
  if (cells.length === 0) {
    return `<p class="no-data">No swaras detected yet — try more mic sensitivity.</p>`;
  }
  const tds = cells
    .map(
      (c) => `<td class="cell ${GRADE_CLASS[c.grade]}">
        <span class="s">${c.sym}</span>
        <span class="g">${GRADE_LABEL[c.grade]}</span>
        <span class="p">${Math.round(c.inTunePct)}%</span>
      </td>`,
    )
    .join('');
  return `
    <div class="table-scroll">
      <table class="heat">
        <caption class="sr-only">Intonation grade per swara — colour, label, and percentage together indicate the grade.</caption>
        <tbody><tr>${tds}</tr></tbody>
      </table>
    </div>`;
}

const SPARK_W = 300;
const SPARK_H = 44;
const SPARK_PAD_Y = 5;
/** One drift bucket roughly every 5s, clamped to 4–12 buckets so a very short
 * take still gets a readable trend and a very long one doesn't overplot. */
function driftBucketCount(durationSec: number): number {
  return Math.min(12, Math.max(4, Math.round(durationSec / 5)));
}

/** The sparkline SVG — an accessible `role="img"` with a text summary of the
 * trend (`driftTrendDescription`) for anyone who can't see the line's shape.
 * A `null` bucket (no held-note frames — silence or an all-glide stretch, see
 * `driftSeries`) breaks the line (pen up) rather than plotting a false dip to
 * zero. Dots mark only the first/last VALID bucket (matching the mockup's
 * spark), not every point — the full per-bucket detail lives in the table
 * fallback below instead of cluttering this small decorative chart. */
function sparklineSvg(points: DriftPoint[]): string {
  const valid = points.filter(
    (p): p is DriftPoint & { centeredPct: number } => p.centeredPct !== null,
  );
  if (valid.length === 0) return '';
  const maxT = points[points.length - 1]!.tSec || 1;
  const xOf = (t: number) => (t / maxT) * SPARK_W;
  const yOf = (pct: number) => SPARK_H - SPARK_PAD_Y - (pct / 100) * (SPARK_H - 2 * SPARK_PAD_Y);

  let path = '';
  let penDown = false;
  for (const p of points) {
    if (p.centeredPct === null) {
      penDown = false;
      continue;
    }
    path += `${penDown ? 'L' : 'M'}${xOf(p.tSec).toFixed(1)},${yOf(p.centeredPct).toFixed(1)} `;
    penDown = true;
  }
  const first = valid[0]!;
  const last = valid[valid.length - 1]!;
  const dots =
    `<circle class="spark-dot" cx="${xOf(first.tSec).toFixed(1)}" cy="${yOf(first.centeredPct).toFixed(1)}" r="3"></circle>` +
    (last !== first
      ? `<circle class="spark-dot" cx="${xOf(last.tSec).toFixed(1)}" cy="${yOf(last.centeredPct).toFixed(1)}" r="3"></circle>`
      : '');
  const trend = driftTrendDescription(points);

  return `<svg class="spark" viewBox="0 0 ${SPARK_W} ${SPARK_H}" preserveAspectRatio="none" role="img"
      aria-label="Well-centred percent over the take. Centring ${trend}.">
      <path class="spark-line" d="${path}"></path>
      ${dots}
    </svg>`;
}

/** The same drift-over-time data as an accessible, collapsed-by-default
 * `<table>` fallback — reachable by keyboard/screen reader without cluttering
 * the sighted layout with a duplicate of the sparkline above it. */
function sparklineTableHtml(points: DriftPoint[]): string {
  const rows = points
    .map(
      (p) =>
        `<tr><td>${mmss(p.tSec)}</td><td>${p.centeredPct === null ? '— (no data)' : `${Math.round(p.centeredPct)}%`}</td></tr>`,
    )
    .join('');
  return `
    <details class="detail">
      <summary>Show centring data as a table</summary>
      <div class="table-scroll">
        <table class="data-table">
          <caption>Well-centred percent by time bucket</caption>
          <thead><tr><th scope="col">Time</th><th scope="col">Well-centred %</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </details>`;
}

function sparklineSectionHtml(
  report: SessionReport,
  recSamples: readonly RecordedSample[],
  saHz: number,
  targetCents: Record<string, number>,
  level: PracticeLevel,
): string {
  const points = driftSeries(
    recSamples,
    saHz,
    targetCents,
    level,
    driftBucketCount(report.durationSec),
  );
  if (points.length === 0) {
    return `<p class="no-data">Not enough of the take was voiced to chart a trend.</p>`;
  }
  const svg = sparklineSvg(points);
  if (!svg) {
    return `<p class="no-data">Not enough of the take was voiced to chart a trend.</p>${sparklineTableHtml(points)}`;
  }
  return `${svg}${sparklineTableHtml(points)}`;
}

/**
 * The child summary's ONE "let's try next" nudge — the single highest-value
 * focus item from child-mode `localCoaching` (already number-free: child-mode
 * `why`/`tip` strings never carry %/¢, per `local-coach.ts`), framed as a
 * gentle suggestion rather than a report-card line item.
 */
function nudgeHtml(focus: CoachingFocus | undefined): string {
  if (!focus) return '';
  return `<div class="eyebrow">Let's try next</div><p class="nudge"><b>${escapeHtml(focus.area)}</b> — ${escapeHtml(focus.tip)}</p>`;
}

/** The nudge's swara, in words — falls back to the raw symbol on the (never
 * expected in practice) chance a symbol isn't in `SWARA_NAMES`. */
function swaraDisplayName(sym: string): string {
  return SWARA_NAMES[sym as SwaraSymbol] ?? sym;
}

/**
 * The child Summary's ONE optional, tap-to-try drill invitation — same
 * target swara as the "let's try next" nudge above it, offered as a warm-up
 * rather than a correction. Callers must only pass a `targetSwara` that's
 * confirmed in-scale for the current raga (`renderResultsPanel` does this via
 * `seedRecord.swara_variants`) — `generateDrill` throws on an out-of-scale
 * swara, so this button must never even render for one.
 */
function drillButtonHtml(targetSwara: string | undefined): string {
  if (!targetSwara) return '';
  const swaraName = swaraDisplayName(targetSwara);
  return `<button type="button" class="btn drill-btn" id="drill-btn" aria-label="Try a warm-up drill for ${escapeHtml(swaraName)}">Try a warm-up for ${escapeHtml(swaraName)} 🎯</button>`;
}

export function renderResultsPanel(container: HTMLElement, deps: ResultsPanelDeps): void {
  // Stop any audio (and its cursor rAF loop) still running from a previous
  // take before replacing the DOM — see results.ts's `stopPlayback` doc.
  stopPlayback();

  const { report, recSamples, saHz, targetCents, level, ragaName } = deps;
  const allowed = ragaSwarasFor(ragaName);
  const seedRecord = ragaSeedFor(ragaName);
  const coachCtx = {
    ragaName,
    // Unused by localCoaching's current logic (kept only for LocalCoachCtx's
    // shape, which also serves the AI-coach request path — see coach-types.ts).
    saNote: '',
    level,
    allowed,
    ...(seedRecord ? { raga: seedRecord } : {}),
  };
  // The child view is built ENTIRELY from child-mode coaching, which already
  // strips %/¢ from its strings (Task 1's `local-coach.ts`) — nothing below
  // re-adds a number. The grown-up section below reuses the original numeric
  // renderers (`metricHtml`/`heatmapHtml`/sparkline) unchanged instead of a
  // second `localCoaching` call, since the design (`encouraging-feedback-
  // design.md`, section B) puts the numeric detail in the metric cards/
  // heatmap/drift chart, not in a duplicated coaching card.
  const note = localCoaching(report, coachCtx, { audience: 'child' });
  const hasPlayback = !!deps.recording && recSamples.length > 0;

  const wentWell = report.inRagaPct >= CHILD_CELEBRATE_PCT;
  const headlineHtml = `<p class="child-headline">${wentWell ? '✨ ' : ''}${escapeHtml(note.encouragement)}</p>`;
  // 1–2 swaras as words, no numbers (child-mode strengths already omit them).
  const strengthsHtml = note.strengths.length
    ? `<div class="eyebrow">What went well</div><ul>${note.strengths
        .slice(0, 2)
        .map((s) => `<li>${escapeHtml(s)}</li>`)
        .join('')}</ul>`
    : '';
  // ONE gentle nudge, not the full focus list.
  const focus = note.focus[0];
  const nextUpHtml = nudgeHtml(focus);
  // Task 4: the SAME focus swara, offered as an optional tap-to-try drill —
  // shown ONLY when there is a focus swara AND it's actually in this raga's
  // scale (`seedRecord` present + the symbol is one of its `swara_variants`
  // keys). `generateDrill` throws on an out-of-scale swara, so this guard
  // must stay in lockstep with what the caller's `onTryDrill` will pass it.
  const drillTargetSwara =
    focus?.swara && seedRecord && focus.swara in seedRecord.swara_variants ? focus.swara : undefined;
  const drillHtml = drillButtonHtml(drillTargetSwara);
  const reviewMiniHtml =
    recSamples.length > 0
      ? `<div class="review-mini">
          <canvas id="review-canvas" role="img" aria-label="Pitch contour for this take"></canvas>
          ${hasPlayback ? '<canvas id="review-cursor" class="cursor-layer"></canvas>' : ''}
        </div>`
      : '';
  const replayHtml = hasPlayback
    ? `<div class="replay">
        <button class="play" id="play-btn" type="button" aria-label="Play recording">▶</button>
        <span>Replay your take — the cursor sweeps the graph.</span>
      </div>`
    : '';

  container.innerHTML = `
    <div class="reveal-note">— your last take —</div>
    <div class="card child-summary">
      ${headlineHtml}
      ${strengthsHtml}
      ${nextUpHtml}
      ${drillHtml}
      ${reviewMiniHtml}
      ${replayHtml}
    </div>
    <div class="grownup-toggle-row">
      <button
        type="button"
        class="grownup-toggle"
        id="grownup-toggle"
        aria-expanded="false"
        aria-controls="grownup-section"
      >
        Grown-up view <span class="chev" aria-hidden="true">▾</span>
      </button>
    </div>
    <div class="grownup-section" id="grownup-section" hidden>
      <div class="metrics">
        ${metricHtml('In the raga', report.inRagaPct)}
        ${metricHtml('Well-centred', report.centeredPct)}
        ${metricHtml('Gamaka adherence', report.gamaka.adherencePct)}
      </div>
      <div class="card">
        <h2>Per-swara</h2>
        ${heatmapHtml(report)}
        <h2 class="spark-heading">Centring over the take</h2>
        ${sparklineSectionHtml(report, recSamples, saHz, targetCents, level)}
      </div>
    </div>
    <div class="new-take-row">
      <button class="btn" id="new-take-btn" type="button">New take</button>
    </div>`;

  // A single ✨ celebration on a good take — same `wentWell` threshold as the
  // headline's ✨ prefix above, so the sparkle and the wording always agree
  // on what counts as "went well". Anchored to the whole child-summary card,
  // which is always laid out by this point (unlike a zero-sized placeholder,
  // which `celebrate()` no-ops on). Fires exactly once per render (this
  // function runs once per completed take, and this call sits in its single
  // synchronous body — no loop, no re-entry), and is itself reduced-motion-
  // aware (celebrate.ts checks `prefers-reduced-motion`).
  if (wentWell) {
    const celebrateAnchor = container.querySelector<HTMLElement>('.child-summary');
    if (celebrateAnchor) celebrate(celebrateAnchor);
  }

  if (recSamples.length > 0) {
    // Deferred a frame so the canvas has its final laid-out size (`clientWidth`/
    // `clientHeight`) before `drawReview` reads it — mirrors the pre-Task-3
    // `renderResults`' own `requestAnimationFrame` wrapping of this same draw.
    requestAnimationFrame(() => {
      const canvas = container.querySelector<HTMLCanvasElement>('#review-canvas');
      if (!canvas) return;
      const mapping = drawReview(
        canvas,
        recSamples,
        saHz,
        allowed,
        targetCents,
        report.episodes,
        level,
      );
      if (hasPlayback && deps.recording && mapping)
        mountPlayback(container, mapping, deps.recording);
    });
  }

  // Grown-up view toggle: quiet, keyboard-operable disclosure. Preference
  // persisted in localStorage (`getGrownupPref`/`setGrownupPref` above),
  // default off, reflected on load — reopening the panel (or a fresh take)
  // shows the analytical detail again without a re-click if a grown-up
  // already opted in.
  const grownupToggle = container.querySelector<HTMLButtonElement>('#grownup-toggle')!;
  const grownupSection = container.querySelector<HTMLElement>('#grownup-section')!;
  function setGrownupExpanded(expanded: boolean): void {
    grownupToggle.setAttribute('aria-expanded', String(expanded));
    grownupSection.hidden = !expanded;
    grownupToggle.classList.toggle('open', expanded);
  }
  setGrownupExpanded(getGrownupPref());
  grownupToggle.addEventListener('click', () => {
    const next = grownupToggle.getAttribute('aria-expanded') !== 'true';
    setGrownupExpanded(next);
    setGrownupPref(next);
  });

  container.querySelector<HTMLButtonElement>('#new-take-btn')?.addEventListener('click', () => {
    stopPlayback();
    container.innerHTML = '';
    deps.onNewTake();
  });

  // Optional, dismissible — only wired when `drillTargetSwara` cleared the
  // guard above, so `drill-btn` only exists in the DOM when it's safe to
  // launch. The button itself never navigates away; the caller decides what
  // "launch" means (Task 2's practice mode).
  if (drillTargetSwara) {
    container.querySelector<HTMLButtonElement>('#drill-btn')?.addEventListener('click', () => {
      deps.onTryDrill(drillTargetSwara);
    });
  }
}
